import React, { useLayoutEffect, useRef, useState } from "react"
import { ChevronRight, Sparkle } from "lucide-react"
import { Trans, useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import type { AgentTurn, FinishedTurn } from "@/reducers/agentPipelineSlice"
import { TextShimmer } from "@/components/ui/text-shimmer"
import { AIChatPipelineSteps } from "./AIChatPipelineSteps"
import { formatTurnDuration, liveLabel } from "./agentTurnPresentation"

// Writes the elapsed time straight into the DOM once a second so the timer
// does not re-render the streaming message under it.
function TurnTimer({ startedAt }: { startedAt: number }) {
  const ref = useRef<HTMLSpanElement>(null)

  useLayoutEffect(() => {
    const tick = () => {
      if (ref.current) {
        ref.current.textContent = formatTurnDuration(Date.now() - startedAt)
      }
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [startedAt])

  return <span ref={ref} className="tabular-nums" />
}

const HEADER_CLASS = "flex items-center gap-1 text-xs text-muted-foreground"

// The status line of a turn. It opens and closes the step list below it
// when there is one; otherwise it is plain text.
function TurnHeader({
  expanded,
  onToggle,
  children,
}: {
  expanded: boolean
  onToggle?: () => void
  children: React.ReactNode
}) {
  if (!onToggle) return <div className={HEADER_CLASS}>{children}</div>
  return (
    <button
      type="button"
      aria-expanded={expanded}
      onClick={onToggle}
      className={cn(HEADER_CLASS, "hover:text-foreground")}
    >
      <ChevronRight
        aria-hidden="true"
        className={cn(
          "size-3.5 shrink-0 transition-transform",
          expanded && "rotate-90",
        )}
      />
      {children}
    </button>
  )
}

/**
 * The one status line of a running turn: the timer. It opens every step of
 * the turn so far.
 */
function AIChatLiveTurn({ turn }: { turn: AgentTurn }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="mb-2 flex flex-col gap-1.5">
      <TurnHeader
        expanded={expanded}
        onToggle={
          turn.steps.length > 0 ? () => setExpanded((open) => !open) : undefined
        }
      >
        <Trans
          t={t}
          i18nKey="ai.turnStatus.inProgress"
          components={{ timer: <TurnTimer startedAt={turn.startedAt} /> }}
        />
      </TurnHeader>
      {expanded && <AIChatPipelineSteps steps={turn.steps} />}
    </div>
  )
}

/**
 * The live row at the bottom of a running turn: the step the agent is on,
 * or "Myślę". The answer arrives in blocks, so between them nothing else on
 * screen moves, and without this row a pause reads as a hang.
 */
function AIChatLiveRow({
  turn,
  className,
}: {
  turn: AgentTurn | undefined
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn("flex items-center gap-2 text-sm", className)}
      data-testid="ai-chat-live-activity"
    >
      <Sparkle
        aria-hidden="true"
        className="size-3.5 shrink-0 animate-pulse text-muted-foreground"
      />
      <TextShimmer duration={1.8}>{liveLabel(t, turn)}</TextShimmer>
    </div>
  )
}

/**
 * What is left of a turn once it is answered: how long it took, folded, with
 * its steps one click away.
 */
function AIChatTurnSummary({ turn }: { turn: FinishedTurn }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="mb-2 flex flex-col gap-1.5">
      <TurnHeader
        expanded={expanded}
        onToggle={() => setExpanded((open) => !open)}
      >
        {t("ai.turnStatus.duration", {
          duration: formatTurnDuration(turn.durationMs),
        })}
      </TurnHeader>
      {expanded && <AIChatPipelineSteps steps={turn.steps} />}
    </div>
  )
}

/**
 * One agent turn as Slick AI shows it, in its own chat and in a driver room:
 * the timer or the time it took over the answer, the live row under it while
 * the agent works.
 */
export function AIChatTurn({
  turn,
  summary,
  answer,
}: {
  // The turn the agent is working on now.
  turn?: AgentTurn
  // An answered turn that had steps.
  summary?: FinishedTurn
  answer: React.ReactNode
}) {
  return (
    <>
      {turn && (
        // A new turn under the same message starts collapsed.
        <AIChatLiveTurn key={turn.startedAt} turn={turn} />
      )}
      {summary && <AIChatTurnSummary turn={summary} />}
      {answer}
      {turn && <AIChatLiveRow turn={turn} className={cn(answer && "mt-3")} />}
    </>
  )
}
